import { flooringAreaValueNamePresenter, formatUsd, groupSubtypePresenter } from '@/Client/Presenters';
import { COLORS2, LAYOUT2, TYPOGRAPHY2 } from '@/Client/Styles';
import { capitalize, compactFlatten, isNumber, isString, pluralize, present } from '@art-suite/art-core-ts';
import { css } from '@emotion/react';
import { ApiOrgProduct } from '@kaiizen/kai-pro-api';
import { useAllOrgSettings, useFeatureFlags } from '@kaiizen/kai-pro-api-react-state';
import {
  createTemplateGroup,
  createTemplateLineItem,
  createTemplateResolutionOption,
  createTemplateTask,
  getAllFlooringAreaValueNames,
  getFlooringAreaValueNames,
  getScanTemplateGroupIfExists,
  getScanTemplateLineItemIfExists,
  getScanTemplateOptionIfExists,
  getScanTemplatePart,
  getScanTemplatePartType,
  getScanTemplateTaskIfExists,
  getStandardLineItem,
  getStandardLineItemGeneratorOptions,
  isReservedFlooringAreaValueName,
  normalizeFlooringAreaValueName,
  ScanTemplateGroup,
  ScanTemplateLineItem,
  ScanTemplateMeasurementValueTypes,
  ScanTemplateOption,
  ScanTemplatePartContext,
  ScanTemplateRoot,
  ScanTemplateTask,
  ScanTemplateValue,
  ScanTemplateValuePresenterNames,
  STANDARD_GROUP_SUBTYPES,
  STANDARD_LINE_ITEM_VALUE_NAMES,
  STANDARD_ROOM_GROUP_VALUES,
  StandardLineItemCostType,
  StandardLineItemGeneratorOptions,
  TaskIdToGroupIdsMap,
  UpdateScanTemplatePart,
} from '@kaiizen/kai-scan-template-system';
import { Fragment, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { ScanTemplateDataMutators, useOrgProduct, useToast } from '../../State';
import { Button, Checkbox, CustomToggle, LabeledInput, LabeledMultiSelect, LabeledSelect } from '../../Widgets';
import { OrgProductDetails } from '../OrgProductDetails';
import { CombinedProductSearchModal } from './CombinedProductSearchModal';
import { getScanTemplateTradeOptions } from './getScanTemplateTradeOptions';
import { OrderableScanTemplatePartList } from './OrderableScanTemplatePartList';
import { ScanTemplateAdvancedValuesEditor } from './ScanTemplateAdvancedValuesEditor';
import { MAX_PART_DESCRIPTION_LENGTH, MAX_TASK_NAME_LENGTH } from './scanTemplateFieldLimits';
import { parseScanTemplateDefaultValueOrEquationString, parseTemplateNumericInput } from './ScanTemplateValueInputLib';

const NAV_PATH_STYLE = css`
  label: NAV_PATH_STYLE;
  display: flex;
  flex-direction: column;
  gap: ${LAYOUT2.spacing1}px;
  ${TYPOGRAPHY2.medium};
`;

const NAV_PATH_ITEM_CONTAINER = css`
  label: NAV_PATH_ITEM_CONTAINER;
  display: flex;
  flex-direction: row;
  gap: ${LAYOUT2.spacing1}px;
  align-items: center;
`;

const NAV_PATH_ITEM_LABEL_STYLE = css`
  label: NAV_PATH_ITEM_LABEL_STYLE;
  color: var(--text4);
`;

const NAV_PATH_ITEM_NAME_STYLE = css`
  label: NAV_PATH_ITEM_NAME_STYLE;
  color: var(--textGreen);
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

const HORIZONTAL_STYLE = css`
  label: HORIZONTAL_STYLE;
  display: flex;
  flex-direction: row;
  gap: 10px;
`;

const SECTION_TITLE_STYLE = css`
  label: SECTION_TITLE_STYLE;
  ${TYPOGRAPHY2.mediumBold};
  padding-top: ${LAYOUT2.spacing1}px;
  padding-bottom: ${LAYOUT2.spacing1}px;
  border-bottom: 1px solid ${COLORS2.alwaysDark.border.darkGray};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TEMPLATE_PART_CHILDREN_LIST_STYLE = css`
  label: TEMPLATE_PART_CHILDREN_LIST_STYLE;
  display: flex;
  flex-direction: column;
  gap: ${LAYOUT2.spacing1}px;
`;

const TEMPLATE_PART_CHILDREN_LIST_HEADER_STYLE = css`
  label: TEMPLATE_PART_CHILDREN_LIST_HEADER_STYLE;
  ${TYPOGRAPHY2.medium};
  color: var(--text4);
  padding-top: ${LAYOUT2.spacing1}px;
  padding-bottom: ${LAYOUT2.spacing1}px;
`;

const TEMPLATE_PART_CHILD_ITEM_STYLE = css`
  label: TEMPLATE_PART_CHILD_ITEM_STYLE;
  ${TYPOGRAPHY2.small};
  height: 20px;
  display: flex;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  min-width: 0;
  align-items: center;
  color: var(--textGreen);
  gap: ${LAYOUT2.spacing1}px;
`;

const TEMPLATE_PART_CHILD_ITEM_COUNT_STYLE = css`
  label: TEMPLATE_PART_CHILD_ITEM_COUNT_STYLE;
  ${TYPOGRAPHY2.small};
  color: var(--text4);
  width: ${LAYOUT2.spacing1p5}px;
`;

const TEMPLATE_PART_CHILD_ITEM_LINK_STYLE = css`
  label: TEMPLATE_PART_CHILD_ITEM_LINK_STYLE;
  display: flex;
  align-items: center;
  gap: ${LAYOUT2.spacing1 * .5}px;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

const TEMPLATE_PART_CHILD_ITEM_IMAGE_STYLE = css`
  label: TEMPLATE_PART_CHILD_ITEM_IMAGE_STYLE;
  width: 20px;
  height: 20px;
  object-fit: cover;
`;

const ADVANCED_VALUES_TOGGLE_WRAPPER_STYLE = css`
  label: ADVANCED_VALUES_TOGGLE_WRAPPER_STYLE;
  display: flex;
  align-items: center;
  gap: ${LAYOUT2.spacing1}px;
`;

const NO_VALUES_STYLE = css`
  label: NO_VALUES_STYLE;
  ${TYPOGRAPHY2.small};
  color: var(--text4);
  padding-top: ${LAYOUT2.spacing1}px;
  padding-bottom: ${LAYOUT2.spacing1}px;
`;

const ROOM_GROUP_CHECKBOX_STYLE = css`
  label: ROOM_GROUP_CHECKBOX_STYLE;
  display: flex;
  align-items: center;
  gap: ${LAYOUT2.spacing1}px;
  cursor: pointer;
  ${TYPOGRAPHY2.small};
  color: var(--text3);
`;

const AMOUNT_PREVIEW_STYLE = css`
  label: AMOUNT_PREVIEW_STYLE;
  display: flex;
  flex-direction: row;
  gap: ${LAYOUT2.spacing1}px;
`;

const AMOUNT_PREVIEW_LABEL_STYLE = css`
  label: AMOUNT_PREVIEW_LABEL_STYLE;
  ${TYPOGRAPHY2.small};
  color: var(--text3);
`;

const NON_STANDARD_VALUES_STYLE = css`
  label: NON_STANDARD_VALUES_STYLE;
  ${TYPOGRAPHY2.smallBold};
  color: var(--text2);
`;

const AMOUNT_PREVIEW_VALUE_STYLE = css`
  label: AMOUNT_PREVIEW_VALUE_STYLE;
  ${TYPOGRAPHY2.smallBold};
  color: var(--textGreen);
`;

const MEASURED_EXAMPLE_STYLE = css`
  label: MEASURED_EXAMPLE_STYLE;
  display: flex;
  flex-direction: column;
  gap: ${LAYOUT2.spacingQuarter}px;
`;

const HIDDEN_BY_DEFAULT_STYLE = css`
  label: HIDDEN_BY_DEFAULT_STYLE;
  ${TYPOGRAPHY2.small};
  color: var(--text3);
  text-align: center;
`;

type DeletionCounts = {
  tasks: number;
  options: number;
  lineItems: number;
  skuIdOptions: number;
};

const countSkuIdOptionsInLineItem = (lineItem: ScanTemplateLineItem): number => lineItem.skuIdOptions?.length || 0;

const countSkuIdOptionsInOption = (option: ScanTemplateOption): number =>
  (option.lineItemTemplates || []).reduce((sum, lineItem) => sum + countSkuIdOptionsInLineItem(lineItem), 0);

const countSkuIdOptionsInTask = (task: ScanTemplateTask): number =>
  (task.optionTemplates || []).reduce((sum, option) => sum + countSkuIdOptionsInOption(option), 0);

const countSkuIdOptionsInGroup = (scanTemplateRoot: ScanTemplateRoot, group: ScanTemplateGroup): number => {
  const tasks = (group.taskTemplateIds?.map(taskId => scanTemplateRoot.taskTemplatesById?.[taskId]).filter((task): task is ScanTemplateTask => !!task)) || [];
  const inlineTasks = group.taskTemplates || [];
  return [...tasks, ...inlineTasks].reduce((sum, task) => sum + countSkuIdOptionsInTask(task), 0);
};

const countLineItemsInTask = (task: ScanTemplateTask): number =>
  (task.optionTemplates || []).reduce((sum, option) => sum + (option.lineItemTemplates?.length || 0), 0);

const countLineItemsInGroup = (scanTemplateRoot: ScanTemplateRoot, group: ScanTemplateGroup): number => {
  const tasks = (group.taskTemplateIds?.map(taskId => scanTemplateRoot.taskTemplatesById?.[taskId]).filter((task): task is ScanTemplateTask => !!task)) || [];
  const inlineTasks = group.taskTemplates || [];
  return [...tasks, ...inlineTasks].reduce((sum, task) => sum + countLineItemsInTask(task), 0);
};

const getDeletionCountsForLineItem = (lineItem: ScanTemplateLineItem): DeletionCounts => ({
  tasks: 0,
  options: 0,
  lineItems: 0,
  skuIdOptions: countSkuIdOptionsInLineItem(lineItem),
});

const getDeletionCountsForOption = (option: ScanTemplateOption): DeletionCounts => ({
  tasks: 0,
  options: 0,
  lineItems: option.lineItemTemplates?.length || 0,
  skuIdOptions: countSkuIdOptionsInOption(option),
});

const getDeletionCountsForTask = (task: ScanTemplateTask): DeletionCounts => ({
  tasks: 0,
  options: task.optionTemplates?.length || 0,
  lineItems: countLineItemsInTask(task),
  skuIdOptions: countSkuIdOptionsInTask(task),
});

const getDeletionCountsForGroup = (scanTemplateRoot: ScanTemplateRoot, group: ScanTemplateGroup): DeletionCounts => {
  const tasks = (group.taskTemplateIds?.map(taskId => scanTemplateRoot.taskTemplatesById?.[taskId]).filter((task): task is ScanTemplateTask => !!task)) || [];
  const inlineTasks = group.taskTemplates || [];
  const allTasks = [...tasks, ...inlineTasks];

  return {
    tasks: allTasks.length,
    options: allTasks.reduce((sum, task) => sum + (task.optionTemplates?.length || 0), 0),
    lineItems: countLineItemsInGroup(scanTemplateRoot, group),
    skuIdOptions: countSkuIdOptionsInGroup(scanTemplateRoot, group),
  };
};

const getDeleteConfirmationMessage = (
  partType: string,
  partName: string,
  counts: DeletionCounts,
): string => {
  const parts: string[] = [];

  if (counts.tasks > 0) parts.push(pluralize(counts.tasks, 'task'));
  if (counts.options > 0) parts.push(pluralize(counts.options, 'option'));
  if (counts.lineItems > 0) parts.push(pluralize(counts.lineItems, 'line item'));
  if (counts.skuIdOptions > 0) parts.push(pluralize(counts.skuIdOptions, 'product references'));

  if (parts.length === 0) {
    return `Are you sure you want to delete this ${partType} "${partName}"?`;
  }

  return `Are you sure you want to delete the ${partType} "${partName}"?\n\nThis will also delete:\n- ${parts.join('\n- ')}\n\nThis action cannot be undone.`;
};

const LineItemChildLineItem = ({ lineItem }: { lineItem: ScanTemplateLineItem }) => {
  const { orgProduct } = useOrgProduct(lineItem.skuIdOptions?.[0] as string);
  return (
    <>
      {orgProduct?.image && (
        <img
          css={TEMPLATE_PART_CHILD_ITEM_IMAGE_STYLE}
          src={orgProduct.image}
        />
      )}
      {orgProduct ? orgProduct.title : lineItem.name}
    </>
  );
};

const getNumberFromValue = (templateValue: ScanTemplateValue, defaultValue: number = 0): number => {
  if (isNumber(templateValue.value)) return templateValue.value;
  if (isString(templateValue.value)) return parseTemplateNumericInput(templateValue.value) ?? defaultValue;
  return defaultValue;
};

const COST_TYPE_OPTIONS: { value: StandardLineItemCostType; label: string }[] = [
  { value: 'material', label: 'Material only' },
  { value: 'labor', label: 'Labor only' },
  { value: 'materialAndLabor', label: 'Material and Labor' },
];

/**
 * The value updates that regenerate a line item template from generator options, keeping what it
 * can of `basedOn`'s values. Regenerating adds AND removes values, and updatePart merges values,
 * so removed value names are explicitly nulled.
 */
const getRegeneratedValueUpdates = (
  lineItem: ScanTemplateLineItem,
  options: StandardLineItemGeneratorOptions,
  basedOn: ScanTemplateLineItem = lineItem,
): Record<string, ScanTemplateValue | null> => ({
  ...Object.fromEntries(Object.keys(lineItem.values ?? {}).map(valueName => [valueName, null])),
  ...getStandardLineItem(options, basedOn).values,
});

const StandardLineItemValuesEditor = ({
  lineItemTemplate,
  option,
  task,
  groupsContainingTask,
  updatePart,
  showToast,
  orgProduct,
  readOnly,
}: {
  lineItemTemplate: ScanTemplateLineItem;
  option?: ScanTemplateOption;
  task?: ScanTemplateTask;
  groupsContainingTask: ScanTemplateGroup[];
  updatePart: (updates: UpdateScanTemplatePart) => void;
  showToast: (message: string, type: 'error' | 'success') => void;
  orgProduct: ApiOrgProduct | undefined;
  readOnly?: boolean;
}) => {
  const values = lineItemTemplate.values ?? {};
  const generatorOptions = getStandardLineItemGeneratorOptions(lineItemTemplate);
  const hasMaterial = generatorOptions.costType !== 'labor';
  const hasLabor = generatorOptions.costType !== 'material';
  const isMeasured = !!generatorOptions.measurement;
  const hasOverage = !!generatorOptions.overage;
  const hasCoverage = !!generatorOptions.coverage;

  // Authoring measured line items is feature-flagged; a template that's already measured
  // keeps its controls so it stays editable regardless of the flag.
  const { featureFlags } = useFeatureFlags();
  const showMeasuredControls = featureFlags.measuredLineItems || isMeasured;

  const hasProduct = present(lineItemTemplate?.skuIdOptions?.[0]);

  const quantityType = values.quantity?.type ?? 'count';
  const quantityString = (values.quantity?.value ?? '').toString();
  const measurementType = values.measurement?.type ?? 'sqft';
  const measurementString = (values.measurement?.value ?? '').toString();
  const laborPriceValue = getNumberFromValue(values.laborPrice ?? { value: 0, type: 'usd' }, 0);
  const materialPriceValue = getNumberFromValue(values.price ?? { value: 0, type: 'usd' }, 0);
  const overageValue = getNumberFromValue(values.overage ?? { value: 0, type: 'count' }, 0);
  const overagePercent = Math.round(overageValue * 1000) / 10;
  const coverageValue = getNumberFromValue(values.coverage ?? { value: 1, type: 'count' }, 1);

  // Value names potentially in scope for quantity/measurement expressions: the parent option's and
  // grandparent task's values are definite; the task can live in multiple groups, so offer
  // the union of value names across the groups that contain it. Shown as exact (lowerCamelCase) value names.
  const inScopeValueNames = useMemo(() => {
    const names = new Set<string>([
      ...Object.keys(option?.values ?? {}),
      ...Object.keys(task?.values ?? {}),
    ]);
    groupsContainingTask.forEach(group => Object.keys(group.values ?? {}).forEach(name => names.add(name)));
    return [...names].sort((a, b) => a.localeCompare(b));
  }, [option, task, groupsContainingTask]);

  const flooringAreaValueNamesInScope = useMemo(
    () => [...new Set(groupsContainingTask.flatMap(group => getFlooringAreaValueNames(group.values)))],
    [groupsContainingTask],
  );

  // Flooring areas first (the common measurement sources), then the other in-scope value names
  const measurementSelectItems = useMemo(() => [
    ...flooringAreaValueNamesInScope.map(name => ({ value: name, label: `${flooringAreaValueNamePresenter(name)} ("${name}")` })),
    ...inScopeValueNames.filter(name => !flooringAreaValueNamesInScope.includes(name)).map(name => ({ value: name })),
  ], [flooringAreaValueNamesInScope, inScopeValueNames]);

  const nonStandardValueNames = Object.keys(values).filter(name => !STANDARD_LINE_ITEM_VALUE_NAMES.includes(name));

  const regenerate = (newOptions: StandardLineItemGeneratorOptions, basedOn: ScanTemplateLineItem = lineItemTemplate) =>
    updatePart({ values: getRegeneratedValueUpdates(lineItemTemplate, newOptions, basedOn) });

  const toggleMeasured = (measured: boolean) => {
    // seed the measurement from a string quantity equation (e.g. a flooring area)
    const quantityValue = values.quantity?.value;
    regenerate(
      measured
        ? { ...generatorOptions, measurement: isString(quantityValue) ? quantityValue : true }
        : { costType: generatorOptions.costType, skuMode: generatorOptions.skuMode },
    );
  };

  return (
    <>
      <div>Options</div>
      <div css={HORIZONTAL_STYLE}>
        <LabeledSelect
          label='Cost Type'
          disabled={readOnly}
          value={generatorOptions.costType}
          items={COST_TYPE_OPTIONS}
          onChange={newCostType => {
            if (newCostType === 'labor' && generatorOptions.skuMode !== 'none') {
              showToast('Remove the product first: a labor-only line item cannot have a product', 'error');
              return;
            }
            regenerate({ ...generatorOptions, costType: newCostType as StandardLineItemCostType });
          }}
        />
      </div>
      {showMeasuredControls && (
        <div css={HORIZONTAL_STYLE}>
          <label css={ROOM_GROUP_CHECKBOX_STYLE}>
            <Checkbox
              checked={isMeasured}
              disabled={readOnly}
              onChange={event => toggleMeasured(event.target.checked)}
            />
            <span>Measured</span>
          </label>
          <label css={ROOM_GROUP_CHECKBOX_STYLE}>
            <Checkbox
              checked={hasOverage && isMeasured}
              disabled={readOnly || !isMeasured}
              onChange={event => regenerate({ ...generatorOptions, overage: event.target.checked || undefined })}
            />
            <span>Use Overage</span>
          </label>

          <label css={ROOM_GROUP_CHECKBOX_STYLE}>
            <Checkbox
              checked={hasCoverage && isMeasured}
              disabled={readOnly || !isMeasured}
              onChange={event => regenerate({ ...generatorOptions, coverage: event.target.checked || undefined })}
            />
            <span>Use Coverage</span>
          </label>
        </div>
      )}

      <div>Values</div>

      {isMeasured
        ? (
          <>
            <div css={HORIZONTAL_STYLE}>
              <LabeledSelect
                label='Measurement'
                disabled={readOnly}
                value={measurementString}
                defaultDisplayValue={measurementString}
                items={measurementSelectItems}
                allowAddNew={!readOnly}
                placeholder='pick a measurement or enter a value'
                onUpdate={newMeasurementString => {
                  const parsed = parseScanTemplateDefaultValueOrEquationString(newMeasurementString) ?? 0;
                  if (isString(parsed)) regenerate({ ...generatorOptions, measurement: parsed });
                  else updatePart({ values: { measurement: { type: measurementType, ...values.measurement, value: parsed } } });
                }}
              />
              <LabeledSelect
                label='Measurement Type'
                disabled={readOnly}
                value={measurementType}
                items={ScanTemplateMeasurementValueTypes.map(type => ({ value: type, label: ScanTemplateValuePresenterNames[type] }))}
                onUpdate={newType =>
                  regenerate(generatorOptions, { ...lineItemTemplate, values: { ...values, measurement: { ...values.measurement, type: newType } } })}
              />
            </div>
            {hasOverage && (
              <div css={HORIZONTAL_STYLE}>
                <LabeledInput
                  label='Overage %'
                  disabled={readOnly}
                  value={overagePercent.toString()}
                  placeholder='0'
                  onUpdate={newPercentString => {
                    const parsedPercent = newPercentString.trim() === '' ? 0 : parseTemplateNumericInput(newPercentString);
                    if (parsedPercent != null) updatePart({ values: { overage: { type: 'count', value: parsedPercent / 100 } } });
                  }}
                />
              </div>
            )}
            {hasCoverage && (
              <div css={HORIZONTAL_STYLE}>
                <LabeledInput
                  label={`Coverage: ${measurementType} per ${hasProduct ? 'SKU' : 'quantity'}`}
                  disabled={readOnly}
                  value={coverageValue.toString()}
                  placeholder='1'
                  onUpdate={newCoverageString => {
                    const parsedCoverage = parseTemplateNumericInput(newCoverageString);
                    if (parsedCoverage != null && parsedCoverage > 0) {
                      updatePart({ values: { coverage: { type: 'count', value: parsedCoverage } } });
                    }
                  }}
                />
              </div>
            )}
            <div css={HORIZONTAL_STYLE}>
              <LabeledInput
                label='Quantity (calculated)'
                value={quantityString}
                disabled={true}
              />
            </div>
          </>
        )
        : (
          <div css={HORIZONTAL_STYLE}>
            <LabeledInput
              label='Default Quantity'
              disabled={readOnly}
              value={quantityString}
              placeholder='add quantity'
              onUpdate={newQuantityString =>
                updatePart({
                  values: {
                    quantity: {
                      value: parseScanTemplateDefaultValueOrEquationString(newQuantityString) ?? 0,
                      type: quantityType,
                    },
                  },
                })}
            />
            <LabeledSelect
              label='Quantity Type'
              value={quantityType}
              disabled={readOnly}
              items={ScanTemplateMeasurementValueTypes.map(type => ({ value: type, label: ScanTemplateValuePresenterNames[type] }))}
              onUpdate={countType => updatePart({ values: { quantity: { ...(values.quantity ?? {}), type: countType } } })}
            />
          </div>
        )}
      <div css={HORIZONTAL_STYLE}>
        {hasLabor && (
          <LabeledInput
            label={`Labor Price (per ${isMeasured ? measurementType : quantityType})`}
            disabled={readOnly}
            value={(values.laborPrice?.value ?? '').toString()}
            placeholder='add labor price'
            onUpdate={laborPriceString => {
              const parsedLaborPrice = parseScanTemplateDefaultValueOrEquationString(laborPriceString) ?? 0;
              updatePart({ values: { laborPrice: { value: parsedLaborPrice, type: 'usd' } } });
            }}
          />
        )}
        {
          /* price CAN be set ONLY when there is no orgProduct; if there is an orgProduct, the price is set by the orgProduct */
          hasMaterial && (
            <LabeledInput
              label={`Material Price (per ${hasProduct ? 'SKU' : hasCoverage ? 'quantity' : isMeasured ? measurementType : quantityType})`}
              value={orgProduct?.price ? formatUsd(orgProduct.price) : (values.price?.value ?? '').toString()}
              disabled={readOnly || !!orgProduct}
              placeholder='add material price'
              onUpdate={priceString => {
                const parsedPrice = parseScanTemplateDefaultValueOrEquationString(priceString) ?? 0;
                updatePart({ values: { price: { value: parsedPrice, type: 'usd' } } });
              }}
            />
          )
        }
      </div>
      {isMeasured
        ? <MeasuredLineItemExample lineItemTemplate={lineItemTemplate} materialPrice={orgProduct?.price ?? materialPriceValue} />
        : (
          <div css={AMOUNT_PREVIEW_STYLE}>
            <div css={AMOUNT_PREVIEW_LABEL_STYLE}>Default Amount:</div>
            <div css={AMOUNT_PREVIEW_VALUE_STYLE}>
              {formatUsd(
                ((hasLabor ? laborPriceValue : 0) + (hasMaterial ? (orgProduct?.price ?? materialPriceValue) : 0))
                  * getNumberFromValue(values.quantity ?? { value: 0, type: 'count' }, 1),
              )}
            </div>
          </div>
        )}
      {nonStandardValueNames.length > 0 && (
        <div css={NON_STANDARD_VALUES_STYLE}>
          {`Non-standard values (edit in Advanced): ${nonStandardValueNames.join(', ')}`}
        </div>
      )}
    </>
  );
};

/** Example measurement used to demonstrate how a measured line item's costs are calculated. */
const DEMO_MEASUREMENT = 120;

/**
 * A concrete worked example replacing the "Default Amount" preview for measured line
 * items. The quantity is derived from a measurement, so a single dollar amount would
 * be misleading. Walks the derivation: measurement, plus overage when configured,
 * divided into whole purchased units when coverage is configured, then material and
 * labor costs (labor is priced against the measurement, never the purchase quantity).
 */
const MeasuredLineItemExample = ({ lineItemTemplate, materialPrice }: { lineItemTemplate: ScanTemplateLineItem; materialPrice: number }) => {
  const values = lineItemTemplate.values ?? {};
  const { costType, overage: hasOverage, coverage: hasCoverage } = getStandardLineItemGeneratorOptions(lineItemTemplate);
  const hasMaterial = costType !== 'labor';
  const hasLabor = costType !== 'material';
  const measurementType = values.measurement?.type ?? 'sqft';
  const measurementName = isString(values.measurement?.value) ? values.measurement.value : 'the measurement';
  const measurementValue = getNumberFromValue(values.measurement ?? { value: 0, type: 'sqft' }, 0);
  const overageValue = getNumberFromValue(values.overage ?? { value: 0, type: 'count' }, 0);
  const overagePercent = Math.round(overageValue * 1000) / 10;
  const coverageValue = getNumberFromValue(values.coverage ?? { value: 1, type: 'count' }, 1);
  const laborPrice = getNumberFromValue(values.laborPrice ?? { value: 0, type: 'usd' }, 0);
  const demoMeasurement = measurementValue > 0 ? measurementValue : DEMO_MEASUREMENT;
  const measuredAmount = Math.round(demoMeasurement * (1 + overageValue) * 10) / 10;
  const purchaseQuantity = hasCoverage ? Math.ceil(measuredAmount / (coverageValue || 1)) : measuredAmount;
  const materialCost = materialPrice * purchaseQuantity;
  const laborCost = laborPrice * demoMeasurement;
  return (
    <div css={MEASURED_EXAMPLE_STYLE}>
      <div css={AMOUNT_PREVIEW_LABEL_STYLE}>
        {`Example, if ${measurementName} measures ${demoMeasurement} ${measurementType}:`}
      </div>
      {hasOverage && (
        <div css={AMOUNT_PREVIEW_LABEL_STYLE}>
          {`${demoMeasurement} ${measurementType} + ${overagePercent}% overage = ${measuredAmount} ${measurementType}`}
        </div>
      )}
      {hasCoverage && (
        <div css={AMOUNT_PREVIEW_LABEL_STYLE}>
          {`${measuredAmount} ${measurementType} at ${coverageValue} ${measurementType} per unit = ${pluralize(purchaseQuantity, 'unit')} (rounded up)`}
        </div>
      )}
      {hasMaterial && (
        <div css={AMOUNT_PREVIEW_STYLE}>
          <div css={AMOUNT_PREVIEW_LABEL_STYLE}>
            {`Material: ${hasCoverage ? pluralize(purchaseQuantity, 'unit') : measurementType} × ${formatUsd(materialPrice)} =`}
          </div>
          <div css={AMOUNT_PREVIEW_VALUE_STYLE}>{formatUsd(materialCost)}</div>
        </div>
      )}
      {hasLabor && (
        <div css={AMOUNT_PREVIEW_STYLE}>
          <div css={AMOUNT_PREVIEW_LABEL_STYLE}>
            {`Labor: ${demoMeasurement} ${measurementType} × ${formatUsd(laborPrice)} =`}
          </div>
          <div css={AMOUNT_PREVIEW_VALUE_STYLE}>{formatUsd(laborCost)}</div>
        </div>
      )}
      {hasMaterial && hasLabor && (
        <div css={AMOUNT_PREVIEW_STYLE}>
          <div css={AMOUNT_PREVIEW_LABEL_STYLE}>Total:</div>
          <div css={AMOUNT_PREVIEW_VALUE_STYLE}>{formatUsd(materialCost + laborCost)}</div>
        </div>
      )}
    </div>
  );
};

const ROOM_VALUE_KEYS = ['roomLength', 'roomWidth', 'roomHeight', 'roomArea', 'roomPerimeter', 'roomWallArea'] as const;

const StandardGroupValuesEditor = (
  { groupTemplate, scanTemplateRoot, updatePart, readOnly }: {
    groupTemplate: ScanTemplateGroup;
    scanTemplateRoot: ScanTemplateRoot;
    updatePart: (updates: UpdateScanTemplatePart) => void;
    readOnly?: boolean;
  },
) => {
  const { featureFlags } = useFeatureFlags();
  const hasRoomValues = !!groupTemplate.values && ROOM_VALUE_KEYS.some(key => key in (groupTemplate.values ?? {}));
  const flooringValueKeys = getFlooringAreaValueNames(groupTemplate.values);

  // Suggest flooring value names already used on any group, to guide consistent naming across groups
  const flooringSuggestions = useMemo(
    () => getAllFlooringAreaValueNames(scanTemplateRoot).map(key => ({ value: key, label: flooringAreaValueNamePresenter(key) })),
    [scanTemplateRoot],
  );

  const handleFlooringAreasChange = (selectedKeys: string[]) => {
    const normalizedSelected = [...new Set(selectedKeys.map(normalizeFlooringAreaValueName).filter(present))];
    const updates: Record<string, ScanTemplateValue | null> = {};
    normalizedSelected.forEach(key => {
      if (flooringValueKeys.includes(key)) return;
      if (isReservedFlooringAreaValueName(key)) {
        alert(`"${key}" is a standard room value and can't be used as a flooring type`);
        return;
      }
      // an existing same-named value (necessarily "...Area"-suffixed) is intentionally replaced
      updates[key] = { type: 'sqft', meaningType: 'flooringAreaMeasurement' };
    });
    flooringValueKeys.forEach(key => {
      if (!normalizedSelected.includes(key)) updates[key] = null;
    });
    if (Object.keys(updates).length > 0) updatePart({ values: updates });
  };

  const handleRoomCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    if (checked) {
      // Set room values
      updatePart({ values: STANDARD_ROOM_GROUP_VALUES });
    } else {
      // Clear all room values by setting them to null
      const clearedValues = Object.fromEntries(
        ROOM_VALUE_KEYS.map(key => [key, null]),
      );
      updatePart({ values: clearedValues });
    }
  };

  return (
    <>
      <label css={ROOM_GROUP_CHECKBOX_STYLE}>
        <Checkbox
          checked={hasRoomValues}
          onChange={handleRoomCheckboxChange}
          disabled={readOnly}
        />
        <span>Use standard Room Group measurements</span>
      </label>
      {hasRoomValues && (
        <>
          <div css={HORIZONTAL_STYLE}>
            <LabeledInput
              label='roomLength' // labels must match the values name!
              value={(groupTemplate.values?.roomLength?.value ?? 'TBD').toString()}
              size='small'
              disabled={true}
            />
            <LabeledInput
              label='roomWidth' // labels must match the values name!
              value={(groupTemplate.values?.roomWidth?.value ?? 'TBD').toString()}
              size='small'
              disabled={true}
            />
          </div>
          <div css={HORIZONTAL_STYLE}>
            <LabeledInput
              label='roomHeight' // labels must match the values name!
              value={(groupTemplate.values?.roomHeight?.value ?? 'TBD').toString()}
              size='small'
              disabled={true}
            />
            <LabeledInput
              label='roomArea' // labels must match the values name!
              value={(groupTemplate.values?.roomArea?.value ?? 'TBD').toString()}
              size='small'
              disabled={true}
            />
          </div>
          <div css={HORIZONTAL_STYLE}>
            <LabeledInput
              label='roomPerimeter' // labels must match the values name!
              value={(groupTemplate.values?.roomPerimeter?.value ?? 'TBD').toString()}
              size='small'
              disabled={true}
            />
            <LabeledInput
              label='roomWallArea' // labels must match the values name!
              value={(groupTemplate.values?.roomWallArea?.value ?? 'TBD').toString()}
              size='small'
              disabled={true}
            />
          </div>
        </>
      )}
      {!!featureFlags.useRoomMeasurements && (
        <LabeledMultiSelect
          label='Flooring Areas'
          value={flooringValueKeys}
          items={flooringSuggestions}
          allowAddNew={!readOnly}
          placeholder='Add flooring types (e.g. carpetArea, tileArea)...'
          onChange={handleFlooringAreasChange}
          disabled={readOnly}
        />
      )}
    </>
  );
};

const STANDARD_GROUP_SUBTYPE_OPTIONS = STANDARD_GROUP_SUBTYPES
  .map(subtype => ({ value: subtype, label: groupSubtypePresenter(subtype) + ` ("${subtype}")` }))
  .sort((a, b) => a.label.localeCompare(b.label));

export const ScanTemplatePartEditorPanel = ({
  groupCategories,
  taskCategories,
  scanTemplatePartContext,
  scanTemplateRoot,
  scanTemplateDataMutators,
  taskIdToGroupsMap,
  onClose,
  setScanTemplatePartContext,
  readOnly,
}: {
  groupCategories: string[];
  taskCategories: string[];
  scanTemplatePartContext: ScanTemplatePartContext;
  scanTemplateRoot: ScanTemplateRoot;
  scanTemplateDataMutators: ScanTemplateDataMutators;
  taskIdToGroupsMap: TaskIdToGroupIdsMap;
  setScanTemplatePartContext: (context: ScanTemplatePartContext) => void;
  onClose: () => void;
  readOnly?: boolean;
}) => {
  const { groupId, taskId, optionId, lineItemId } = scanTemplatePartContext;
  const scanTemplatePartType = getScanTemplatePartType(scanTemplatePartContext);

  const scanTemplatePart = getScanTemplatePart(scanTemplateRoot, scanTemplatePartContext);

  const { allOrgSettings } = useAllOrgSettings();
  const { showToast } = useToast();
  const { featureFlags } = useFeatureFlags();
  const [isAddOrgProductModalOpen, setIsAddOrgProductModalOpen] = useState(false);
  const [isAdvancedValues, setIsAdvancedValues] = useState(false);
  const [nameFieldResetToken, setNameFieldResetToken] = useState(0);
  const { orgProduct } = useOrgProduct(scanTemplatePart?.skuIdOptions?.[0] as string);
  const navigate = useNavigate();

  const isTask = taskId && scanTemplatePartType === 'taskTemplate';
  const isOption = taskId && optionId && scanTemplatePartType === 'optionTemplate';
  const isGroup = groupId && scanTemplatePartType === 'groupTemplate';
  const isLineItem = scanTemplatePartType === 'lineItemTemplate';

  const group: ScanTemplateGroup | undefined = getScanTemplateGroupIfExists(scanTemplateRoot, groupId);
  const task: ScanTemplateTask | undefined = getScanTemplateTaskIfExists(scanTemplateRoot, group, taskId);
  const option: ScanTemplateOption | undefined = getScanTemplateOptionIfExists(task, optionId);
  const lineItem: ScanTemplateLineItem | undefined = getScanTemplateLineItemIfExists(option, lineItemId);
  const tradeOptions = getScanTemplateTradeOptions({
    configuredTrades: allOrgSettings?.tradeSettings?.trades,
    currentTrade: task?.trade,
  });

  const handleAddOrgProduct = (orgProduct: ApiOrgProduct) => {
    if (!scanTemplatePart) return;
    const lineItemTemplate = scanTemplatePart as ScanTemplateLineItem;
    const currentOptions = getStandardLineItemGeneratorOptions(lineItemTemplate);
    updatePart({
      skuIdOptions: [orgProduct.id],
      values: getRegeneratedValueUpdates(lineItemTemplate, {
        ...currentOptions,
        costType: currentOptions.costType === 'labor' ? 'materialAndLabor' : currentOptions.costType, // a product implies a material cost
        skuMode: 'sku',
      }),
    });
    setIsAddOrgProductModalOpen(false);
  };

  const updatePart = (updates: UpdateScanTemplatePart) => {
    if (!scanTemplatePart) return;
    scanTemplateDataMutators.updatePart({
      groupId,
      taskId,
      optionId,
      lineItemId,
      updates,
    });
  };

  const addChildPart = () => {
    if (!scanTemplatePart) return;
    if (isTask) {
      const newOption = scanTemplateDataMutators.addResolutionOption(groupId, taskId, createTemplateResolutionOption({ name: 'New Option' }));
      setScanTemplatePartContext({ ...scanTemplatePartContext, optionId: newOption.id, lineItemId: undefined });
    } else if (isOption) {
      const newLineItem = scanTemplateDataMutators.addLineItem(
        groupId,
        taskId,
        optionId,
        createTemplateLineItem({ name: 'New Line Item', values: { quantity: { value: 0, type: 'count' } } }),
      );
      setScanTemplatePartContext({ ...scanTemplatePartContext, lineItemId: newLineItem.id });
    } else if (isGroup) {
      const newTask = scanTemplateDataMutators.addTask(groupId!, createTemplateTask({ name: 'New Task' }));
      setScanTemplatePartContext({ ...scanTemplatePartContext, taskId: newTask.id });
    }
  };

  const addSiblingPart = () => {
    if (!scanTemplatePart) return;

    if (isLineItem && optionId && taskId) {
      const newLineItem = scanTemplateDataMutators.addLineItem(
        groupId,
        taskId,
        optionId,
        createTemplateLineItem({ name: 'New Line Item', values: { quantity: { value: 0, type: 'count' } } }),
      );
      setScanTemplatePartContext({ groupId, taskId, optionId, lineItemId: newLineItem.id });
    } else if (isOption && taskId) {
      const newOption = scanTemplateDataMutators.addResolutionOption(groupId, taskId, createTemplateResolutionOption({ name: 'New Option' }));
      setScanTemplatePartContext({ groupId, taskId, optionId: newOption.id, lineItemId: undefined });
    } else if (isTask && taskId) {
      const newTask = scanTemplateDataMutators.addTask(groupId, createTemplateTask({ name: 'New Task' }));
      setScanTemplatePartContext({ groupId, taskId: newTask.id, optionId: undefined, lineItemId: undefined });
    } else if (isGroup && groupId) {
      scanTemplateDataMutators.addGroup(createTemplateGroup({ name: 'New Group' }));
    }
  };

  const handleParentGroupsChange = (selectedGroupIds: string[]) => {
    if (!task) return;

    const currentGroupIds = (usedInGroups || []).map(g => g.id);
    const groupsToAdd = selectedGroupIds.filter(id => !currentGroupIds.includes(id));
    const groupsToRemove = currentGroupIds.filter(id => !selectedGroupIds.includes(id));

    // Remove task from groups that were deselected
    groupsToRemove.forEach(groupIdToRemove => {
      const groupToUpdate = scanTemplateRoot.groupTemplates?.find(g => g.id === groupIdToRemove);
      if (groupToUpdate) {
        scanTemplateDataMutators.updateGroup(groupIdToRemove, {
          taskTemplateIds: (groupToUpdate.taskTemplateIds || []).filter(id => id !== task.id),
        });
      }
    });

    // Add task to newly selected groups
    groupsToAdd.forEach(groupIdToAdd => {
      const groupToUpdate = scanTemplateRoot.groupTemplates?.find(g => g.id === groupIdToAdd);
      if (groupToUpdate) {
        scanTemplateDataMutators.updateGroup(groupIdToAdd, {
          taskTemplateIds: [...(groupToUpdate.taskTemplateIds || []), task.id],
        });
      }
    });
  };

  const handleTasksInGroupChange = (selectedTaskIds: string[]) => {
    if (!group) return;
    scanTemplateDataMutators.updateGroup(group.id, {
      taskTemplateIds: selectedTaskIds,
    });
  };

  const handleRemoveTaskFromGroup = () => {
    if (!group || !taskId) return;
    scanTemplateDataMutators.updateGroup(group.id, {
      taskTemplateIds: (group.taskTemplateIds || []).filter(id => id !== taskId),
    });
    setTimeout(() => onClose(), 100);
  };

  const handleDelete = () => {
    if (!scanTemplatePart) return;

    let counts: DeletionCounts;
    let partTypeLabel: string;

    switch (scanTemplatePartType) {
      case 'lineItemTemplate':
        if (!lineItem) return;
        counts = getDeletionCountsForLineItem(lineItem);
        partTypeLabel = 'line item';
        break;
      case 'optionTemplate':
        if (!option) return;
        counts = getDeletionCountsForOption(option);
        partTypeLabel = 'option';
        break;
      case 'taskTemplate':
        if (!task) return;
        counts = getDeletionCountsForTask(task);
        partTypeLabel = 'task';
        break;
      case 'groupTemplate':
        if (!group) return;
        counts = { tasks: 0, options: 0, lineItems: 0, skuIdOptions: 0 }; // deleting a group doesn't delete any tasks, options, line items, or sku id options
        partTypeLabel = 'group';
        break;
      default:
        return;
    }

    const confirmMessage = getDeleteConfirmationMessage(partTypeLabel, scanTemplatePart.name, counts);

    if (confirm(confirmMessage)) {
      switch (scanTemplatePartType) {
        case 'lineItemTemplate':
          if (taskId && optionId && lineItemId) {
            scanTemplateDataMutators.deleteLineItem(groupId, taskId, optionId, lineItemId);
          }
          break;
        case 'optionTemplate':
          if (taskId && optionId) {
            scanTemplateDataMutators.deleteResolutionOption(groupId, taskId, optionId);
          }
          break;
        case 'taskTemplate':
          if (taskId) {
            scanTemplateDataMutators.removeTask(groupId, taskId);
          }
          break;
        case 'groupTemplate':
          if (groupId) {
            scanTemplateDataMutators.deleteGroup(groupId);
          }
          break;
      }
      onClose();
    }
  };

  const sortedTaskTemplates = useMemo(
    () => (scanTemplateRoot.taskTemplatesById && Object.values(scanTemplateRoot.taskTemplatesById)?.sort((a, b) => a.name.localeCompare(b.name))) || [],
    [scanTemplateRoot.taskTemplatesById],
  );

  if (!scanTemplatePart) return null;
  const name = scanTemplatePart.name;
  const description = scanTemplatePart.description || '';
  const updateName = (newName: string) => {
    if (newName.trim() === '') {
      showToast('Name cannot be empty', 'error');
      setNameFieldResetToken(token => token + 1); // force the Name input to remount and revert to the last saved name
      return;
    }
    updatePart({ name: newName });
  };
  const category = scanTemplatePart.category || '';
  const alwaysShowAdvancedValues = scanTemplatePartType === 'taskTemplate';

  let valuesSection: React.ReactNode | null = null;
  valuesSection = (
    <>
      <div css={SECTION_TITLE_STYLE}>
        <span>
          {isTask
            ? "Task's Custom Values (Measurements)"
            : isGroup
            ? "Group's Custom Values (Measurements)"
            : isOption
            ? "Option's Custom Values (Measurements)"
            : "Line Item's Values"}
        </span>
        <div css={ADVANCED_VALUES_TOGGLE_WRAPPER_STYLE}>
          {!alwaysShowAdvancedValues && (
            <CustomToggle
              isOn={isAdvancedValues}
              alwaysDark={false}
              onToggle={() => setIsAdvancedValues(!isAdvancedValues)}
              offLabel='Standard'
              onLabel='Advanced'
            />
          )}
        </div>
      </div>
      {isAdvancedValues || alwaysShowAdvancedValues
        ? (
          <ScanTemplateAdvancedValuesEditor
            values={scanTemplatePart.values}
            onUpdateValues={values => updatePart({ values })}
            readOnly={readOnly}
          />
        )
        : (scanTemplatePartType === 'lineItemTemplate'
          ? (
            <StandardLineItemValuesEditor
              lineItemTemplate={scanTemplatePart as ScanTemplateLineItem}
              option={option}
              task={task}
              groupsContainingTask={(task && taskIdToGroupsMap[task.id]) || []}
              updatePart={updatePart}
              showToast={showToast}
              orgProduct={orgProduct}
              readOnly={readOnly}
            />
          )
          : isOption
          ? <div css={NO_VALUES_STYLE}>Standard Option Templates have no values</div>
          : isGroup
          ? (
            <StandardGroupValuesEditor
              groupTemplate={scanTemplatePart as ScanTemplateGroup}
              scanTemplateRoot={scanTemplateRoot}
              updatePart={updatePart}
              readOnly={readOnly}
            />
          )
          : null)}
    </>
  );

  let orgProductSection: React.ReactNode | null = null;
  if (isLineItem) {
    orgProductSection = (
      <>
        <div css={SECTION_TITLE_STYLE}>Line Item SKU (Product)</div>
        {orgProduct && <OrgProductDetails orgProduct={orgProduct} />}
        {!readOnly && orgProduct && !orgProduct.productId && (
          <Button
            onClick={() => {
              navigate(`/product/${orgProduct.id}`);
            }}
          >
            Edit Custom Product Details
          </Button>
        )}
        {!readOnly && (
          <Button
            onClick={() => {
              setIsAddOrgProductModalOpen(true);
            }}
          >
            {orgProduct ? 'Change Product' : 'Add Product'}
          </Button>
        )}
        {!readOnly && orgProduct && (
          <Button
            buttonStyle='red'
            onClick={() => {
              if (!confirm('Are you sure you want to remove this product?') || !lineItem) return;
              const skuMode = lineItem.useScopeStageSkus ? 'scopeTimeSku' : 'none';
              updatePart({
                skuIdOptions: [],
                values: getRegeneratedValueUpdates(lineItem, { ...getStandardLineItemGeneratorOptions(lineItem), skuMode }),
              });
            }}
          >
            Remove Product
          </Button>
        )}
        {!readOnly && (
          <CombinedProductSearchModal
            isOpen={isAddOrgProductModalOpen}
            onClose={() => setIsAddOrgProductModalOpen(false)}
            onAdd={handleAddOrgProduct}
          />
        )}
        {/* A line item that already allows scope-time product selection keeps its toggle so it can be turned off regardless of the flag */}
        {(featureFlags.scopeTimeShopping || !!lineItem?.useScopeStageSkus) && (
          <label css={ROOM_GROUP_CHECKBOX_STYLE}>
            <Checkbox
              checked={!!lineItem?.useScopeStageSkus}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                if (!lineItem) return;
                const checked = event.target.checked;
                const currentOptions = getStandardLineItemGeneratorOptions(lineItem);
                if (checked && currentOptions.costType === 'labor') {
                  showToast('A labor-only line item cannot have a product; add a material cost first', 'error');
                  return;
                }
                const skuMode = checked ? 'scopeTimeSku' : lineItem.skuIdOptions?.length ? 'sku' : 'none';
                updatePart({ useScopeStageSkus: checked, values: getRegeneratedValueUpdates(lineItem, { ...currentOptions, skuMode }) });
              }}
              disabled={readOnly}
            />
            <span>Allow product selection during scoping</span>
          </label>
        )}
      </>
    );
  }

  const usedInGroups = task ? taskIdToGroupsMap[task.id] : undefined;

  const navPathParts: { label: string; name: string; context: ScanTemplatePartContext }[] = compactFlatten([
    // usedInGroups?.map(group => ({ label: "group", name: group.name, context: { groupId: group.id } })) ?? [],
    task ? [{ label: 'task', name: task.name, context: { groupId, taskId: task.id } }] : [],
    option ? [{ label: 'option', name: option.name, context: { groupId, taskId, optionId: option.id } }] : [],
    lineItem ? [{ label: 'lineItem', name: lineItem.name, context: { groupId, taskId, optionId, lineItemId: lineItem.id } }] : [],
  ]).slice(0, -1); // slice to get all but last one

  const locationSection: React.ReactNode = (
    <>
      {isTask && <div css={SECTION_TITLE_STYLE}>Task's Groups</div>}
      {isTask && (
        <LabeledMultiSelect
          label='Used in Groups'
          value={(usedInGroups || []).map(g => g.id)}
          items={(scanTemplateRoot.groupTemplates || []).map(g => ({ value: g.id, label: g.name, link: `?groupId=${g.id}` }))}
          onChange={handleParentGroupsChange}
          placeholder='add or remove...'
          disabled={readOnly}
        />
      )}
      {isGroup && <div css={SECTION_TITLE_STYLE}>Group's Tasks</div>}
      {isGroup && (
        <LabeledMultiSelect
          label='Tasks in Group'
          value={group?.taskTemplateIds || []}
          items={sortedTaskTemplates.map(t => ({
            value: t.id,
            label: t.name,
            link: `?taskId=${t.id}`,
          }))}
          onChange={handleTasksInGroupChange}
          placeholder='add or remove...'
          disabled={readOnly}
        />
      )}
      {isOption && <div css={SECTION_TITLE_STYLE}>Option's Task</div>}
      {isLineItem && <div css={SECTION_TITLE_STYLE}>Line Item's Task and Option</div>}
      {navPathParts.length > 0 && (
        <div css={NAV_PATH_STYLE}>
          {navPathParts.map(({ label, name, context }) => (
            <Fragment key={`${context.groupId ?? ''}-${context.taskId ?? ''}-${context.optionId ?? ''}-${context.lineItemId ?? ''}`}>
              <div css={NAV_PATH_ITEM_CONTAINER}>
                <span css={NAV_PATH_ITEM_LABEL_STYLE}>{capitalize(label)}:</span>
                <span
                  css={NAV_PATH_ITEM_NAME_STYLE}
                  onClick={() =>
                    setScanTemplatePartContext(context)}
                >
                  {name}
                </span>
              </div>
            </Fragment>
          ))}
        </div>
      )}
      {/* add sibling */}
      {!readOnly && !isGroup && !isTask && (
        <Button onClick={addSiblingPart}>
          {isOption
            ? 'Add new Option to parent task'
            : isLineItem
            ? 'Add new Line Item to parent option'
            : 'Add Group'}
        </Button>
      )}

      {(isTask && task) && (
        <div css={TEMPLATE_PART_CHILDREN_LIST_STYLE}>
          <div css={SECTION_TITLE_STYLE}>Task's Options</div>
          {task.optionTemplates && task.optionTemplates.length > 0 && (
            <OrderableScanTemplatePartList
              scanTemplateParts={task.optionTemplates}
              onReorder={optionTemplates => {
                scanTemplateDataMutators.updateTask(groupId, taskId, {
                  optionTemplates,
                });
              }}
              onSelectPart={option => {
                setScanTemplatePartContext({ groupId, taskId, optionId: option.id });
              }}
              renderPart={part => part.name}
              readOnly={readOnly}
            />
          )}
        </div>
      )}

      {(isOption && option) && (
        <div css={TEMPLATE_PART_CHILDREN_LIST_STYLE}>
          <div css={SECTION_TITLE_STYLE}>Option's Line Items</div>
          {option.lineItemTemplates && option.lineItemTemplates.length > 0 && (
            <OrderableScanTemplatePartList
              scanTemplateParts={option.lineItemTemplates}
              onReorder={lineItemTemplates => {
                scanTemplateDataMutators.updateResolutionOption(groupId, taskId, optionId, {
                  lineItemTemplates,
                });
              }}
              onSelectPart={lineItem => {
                setScanTemplatePartContext({ groupId, taskId, optionId, lineItemId: lineItem.id });
              }}
              renderPart={part => <LineItemChildLineItem lineItem={part} />}
              readOnly={readOnly}
            />
          )}
        </div>
      )}
      {/* add child */ !readOnly && (isTask || isOption || isGroup) && (
        <Button onClick={addChildPart}>
          {isGroup
            ? 'Add New Task to this group'
            : isTask
            ? 'Add New Option to this task'
            : 'Add New Line Item to this option'}
        </Button>
      )}
    </>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: `${LAYOUT2.spacing2}px` }}>
      <LabeledInput
        key={`${scanTemplatePart.id}-name-${nameFieldResetToken}`}
        label='Name'
        value={name}
        placeholder='Enter a name'
        maxLength={MAX_TASK_NAME_LENGTH}
        showCharacterCount={true}
        disabled={readOnly}
        onUpdate={updateName}
      />
      <LabeledInput
        label={isLineItem ? 'Description/Instructions' : 'Description'}
        value={description}
        placeholder='Enter a description'
        multiline={true}
        rows={3}
        maxLength={MAX_PART_DESCRIPTION_LENGTH}
        showCharacterCount={true}
        disabled={readOnly}
        onUpdate={description => updatePart({ description })}
      />
      {(isGroup || isTask) && (
        <LabeledSelect
          label='Category'
          value={category}
          allowAddNew={!readOnly}
          disabled={readOnly}
          placeholder='Enter a category'
          onUpdate={category => updatePart({ category })}
          items={isGroup
            ? Array.from(groupCategories).map(category => ({ value: category, label: category }))
            : Array.from(taskCategories).map(category => ({ value: category, label: category }))}
        />
      )}
      {isTask && (
        <LabeledSelect
          label='Trade'
          disabled={readOnly}
          value={task?.trade ?? ''}
          items={[
            { value: '', label: 'No trade selected (general contractor will be used)' },
            ...tradeOptions,
          ]}
          onChange={trade => updatePart({ trade: trade === '' ? null as any : trade })}
        />
      )}
      {isGroup && (
        <LabeledSelect
          label='Group Type'
          disabled={readOnly}
          value={group?.subtype ?? ''}
          items={[
            { value: '', label: 'No special group type' },
            ...STANDARD_GROUP_SUBTYPE_OPTIONS,
          ]}
          onChange={subtype => {
            if (subtype !== '') { // check there are no other groups with this subtype
              const otherGroupsWithSameSubtype = scanTemplateRoot.groupTemplates?.find(g => g.subtype === subtype);
              if (otherGroupsWithSameSubtype) {
                showToast('There is already another group with subtype "' + subtype + '": ' + otherGroupsWithSameSubtype.name, 'error');
                return;
              }
            }
            updatePart({ subtype: subtype === '' ? null as any : subtype });
          }}
        />
      )}
      {isTask && (
        <LabeledSelect
          label='Task Type'
          disabled={readOnly}
          value={task?.subtype ?? ''}
          items={[
            { value: '', label: 'No task type selected' },
            { value: 'otherTask', label: 'Non-standard task ("otherTask")' },
          ]}
          onChange={subtype => updatePart({ subtype: subtype === '' ? null as any : subtype, hidden: subtype === 'otherTask' })}
        />
      )}
      {isTask && (
        <LabeledInput
          label='GL Code'
          disabled={readOnly}
          value={task?.customerMetadata?.GLCode ? String(task.customerMetadata.GLCode) : ''}
          placeholder='enter general ledger code'
          maxLength={MAX_TASK_NAME_LENGTH}
          showCharacterCount={true}
          onUpdate={glCode => {
            const currentMetadata = task?.customerMetadata ?? {};
            updatePart({
              customerMetadata: {
                ...currentMetadata,
                GLCode: glCode.trim() === '' ? null as any : glCode.trim(),
              },
            });
          }}
        />
      )}
      {scanTemplatePart.hidden && <div css={HIDDEN_BY_DEFAULT_STYLE}>(Hidden by default in new scans.)</div>}
      {valuesSection}
      {orgProductSection}
      {locationSection}
      {!readOnly && (
        <>
          <div css={SECTION_TITLE_STYLE}>Danger Section</div>
          {isTask && group
            ? (
              <Button
                buttonStyle='red'
                onClick={handleRemoveTaskFromGroup}
              >
                Remove Task from Group
              </Button>
            )
            : isTask && usedInGroups && usedInGroups.length > 0
            ? 'Remove Task from all Groups in order to delete it'
            : (
              <Button
                buttonStyle='red'
                onClick={handleDelete}
              >
                Delete this {scanTemplatePartType === 'lineItemTemplate' ? 'line item' : scanTemplatePartType}
              </Button>
            )}
        </>
      )}
    </div>
  );
};
